import { alpha, makeStyles, Menu, MenuItem, StatusDot, useTheme } from '@paintscout/ui'
import type { MuiTheme as Theme } from '@paintscout/ui'
import { getStatus, getStatusOptions, setStatus } from '@paintscout/util/builder'
import type { QuoteStatus } from 'paintscout'
import classnames from 'classnames'
import React, { useState } from 'react'
import { useQuote } from '../../context'
import { useConfirmStatusChange } from '../../util/useConfirmStatusChange'
import { activatable } from './activatable'

const useStyles = makeStyles(
  (theme: Theme) => ({
    // A tinted pill in the status colour: the dot and the label take the colour itself, the
    // background a light wash of it (the prototype's S95 tints).
    pill: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: theme.spacing(0.75),
      height: 28,
      padding: theme.spacing(0, 1),
      borderRadius: 10,
      fontSize: theme.typography.pxToRem(14),
      lineHeight: 'normal',
      whiteSpace: 'nowrap',
      // Sits under the document ID in the header's right-aligned doc block.
      marginTop: theme.spacing(1),
      color: 'var(--status-color)',
      backgroundColor: 'var(--status-bg)',
      transition: 'background-color 150ms ease'
    },
    clickable: {
      cursor: 'pointer',
      '&:hover, &[aria-expanded="true"]': {
        backgroundColor: 'var(--status-bg-hover)'
      }
    },
    dot: {
      width: 10,
      height: 10,
      borderRadius: '50%',
      flexShrink: 0,
      backgroundColor: 'var(--status-color)'
    }
  }),
  { name: 'QuoteDocumentStatusPill' }
)

// The only statuses a read-only viewer sees (ENG-2187). Internal pipeline states (draft, sent, on
// hold, viewed, invoiced) are for the contractor, not the customer.
const READ_ONLY_STATUSES: QuoteStatus[] = ['accepted', 'declined', 'partial', 'paid']

/**
 * The estimate's status as a pill under the document ID (ENG-2031). In the editor it opens the same
 * status list the Details card uses and saves through `setStatus`, after the same "no automatic
 * emails" warning the Estimate Settings dialog shows; read-only viewers (the customer view, the
 * preview) get the pill without the menu, and only for the statuses in `READ_ONLY_STATUSES`.
 */
export default function QuoteDocumentStatusPill() {
  const classes = useStyles()
  const theme = useTheme<Theme>()
  const { quote, isEditable, updateQuote } = useQuote()
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)

  const isInvoice = !!quote?.is_invoice
  const status = getStatus({ quote }) as QuoteStatus
  const statusOptions = getStatusOptions({ isInvoice })
  const label = statusOptions.find((opt) => opt.value === status)?.label
  const confirmStatusChange = useConfirmStatusChange({ isInvoice })

  // The same table StatusDot draws from; an unknown status falls back to the draft grey.
  const statusColors = theme.palette.quote.status
  const color = statusColors[status] ?? statusColors.draft

  if (!status || !label) return null
  if (!isEditable && !READ_ONLY_STATUSES.includes(status)) return null

  const handleSelect = (value: QuoteStatus) => {
    setAnchorEl(null)
    if (value === status) return
    confirmStatusChange(() => updateQuote({ quote: setStatus({ quote, status: value, updateDates: true }) }))
  }

  return (
    <>
      <span
        className={classnames(classes.pill, { [classes.clickable]: isEditable })}
        // The status colour comes from the theme at runtime, so it is handed to the class as custom
        // properties rather than written as one class per status.
        style={
          {
            '--status-color': color,
            '--status-bg': alpha(color, 0.12),
            '--status-bg-hover': alpha(color, 0.22)
          } as React.CSSProperties
        }
        aria-expanded={isEditable ? Boolean(anchorEl) : undefined}
        aria-haspopup={isEditable ? 'true' : undefined}
        data-testid="quote-status-pill"
        {...activatable(isEditable, setAnchorEl)}
      >
        <span className={classes.dot} />
        {label}
      </span>

      {isEditable && (
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          getContentAnchorEl={null}
        >
          {statusOptions.map((opt) => (
            <MenuItem
              key={opt.value}
              selected={opt.value === status}
              onClick={() => handleSelect(opt.value as QuoteStatus)}
            >
              <StatusDot status={opt.value as QuoteStatus} size={8}>
                {opt.label}
              </StatusDot>
            </MenuItem>
          ))}
        </Menu>
      )}
    </>
  )
}
